<script setup lang="ts">
import { ref } from 'vue';
import { Field, useForm } from 'vee-validate';
import { dateHelpers, QuestionSSN, QuestionYesNo } from '@hfc/ui-core';
import { DentalAndVisionApplicant } from '@/application/shared/domain/DentalAndVisionApplicant';
import { AppTypeDentalOrVision } from '@/application/shared/domain/DentalAndVisionApplication';
import { type Applicant } from '@/application/shared/domain/Applicant';
import { MEDICARE_STATUSES } from '@hfc/common/src/types/MedicareStatuses';
import { useDentalAndVisionApplication } from '@/shared/infrastructure/composition/useDentalAndVisionApplication';
import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';
import QuestionPersonFullName from '@/application/shared/infrastructure/components/questions/QuestionPersonFullName.vue';
import QuestionEligibleMedicare from '@/application/shared/infrastructure/components/questions/QuestionEligibleMedicare.vue';
import AddressValidationElement from '@/application/shared/infrastructure/components/AddressValidationElement.vue';

const { fromTimestampToFormatted } = dateHelpers();
const { handleSubmit } = useForm();
const runValidations = handleSubmit(() => true);

const props = withDefaults(defineProps<{
  applicationType: AppTypeDentalOrVision;
  obfuscateSsn?: boolean;
}>(), {
  obfuscateSsn: true
});

const yesNoQuestionEngaged = ref(false);
const {
  dentalAndVisionApplicationStore,
  notPrimaryApplicants,
  primaryApplicant,
  application
} = useDentalAndVisionApplication(props.applicationType);

const castedApplicantsSSNs = (application.value.applicants as Array<Applicant>).map((applicant: Applicant) => applicant.ssn ?? '').filter(Boolean);

const sameAsMainChange = (applicant: DentalAndVisionApplicant, value?: boolean): void => {
  dentalAndVisionApplicationStore.updateApplicant({ ...applicant, is_address_same_as_main: value }, props.applicationType, applicant.main_index);
};

const hasSSNChange = (applicant: DentalAndVisionApplicant, value?: boolean): void => {
  yesNoQuestionEngaged.value = true;
  const ssn = value ? applicant.ssn : undefined;
  dentalAndVisionApplicationStore.updateApplicant({ ...applicant, has_ssn: value, ssn }, props.applicationType, applicant.main_index);
};

const updateEligibleMedicare = (applicant: DentalAndVisionApplicant, value?: MEDICARE_STATUSES): void => {
  dentalAndVisionApplicationStore.updateApplicant({ ...applicant, eligible_medicare: value }, props.applicationType, applicant.main_index);
};

const emitChange = (value:Partial<DentalAndVisionApplicant>, applicant: DentalAndVisionApplicant): void => {
  dentalAndVisionApplicationStore.updateApplicant({ ...applicant, ...value }, props.applicationType, applicant.main_index);
};

const sectionSubmit = async (): Promise<boolean> => {
  const localValid = await runValidations();

  return Boolean(localValid);
};

defineExpose({
  sectionSubmit: sectionSubmit
});
</script>
<template>
  <BaseSection class="">
    <div v-for="(applicant, i) in notPrimaryApplicants" :key="i">
      <h4 class="mb-4 capitalize">{{ applicant.type }} <span v-if="applicant.index !== undefined">{{ applicant.index + 1 }}</span></h4>
      <div class="flex gap-14 italic text-gray-500 text-lg mb-2">
        <p>Who was born on {{ fromTimestampToFormatted(applicant.date_of_birth, 'MM/DD/YYYY') }} (<span class="capitalize">{{ applicant.gender }}</span>)?</p>
      </div>
      <QuestionPersonFullName :value="applicant" @update:value="v => emitChange(v, applicant)" :auto-focus="i === 0" :hide-suffix="true" :name-prefix="`applicant_${i}_`" />
      <div class="flex-1">
        <Field v-model="applicant.has_ssn" :name="`applicant_${i}_has_ssn`" v-slot="{ field, meta, errorMessage }" :rules="{ yes_no_required: true }" class="relative">
          <QuestionYesNo question-text="" id="hasSSN" @update:modelValue="(v?: boolean) => hasSSNChange(applicant, v)" name="hasSSN" v-model="field.value">
            <template #question>
              <span>Does this person have a Social Security Number?</span>
            </template>
          </QuestionYesNo>
          <span class="error"><span v-if="meta.touched">{{ errorMessage }}</span></span>
        </Field>
      </div>
      <div class="mb-4" v-if="applicant.has_ssn === true">
        <QuestionSSN :value="applicant.ssn" @update:value="emitChange({ ssn: $event }, applicant)" :auto-focus="yesNoQuestionEngaged" :obfuscate="obfuscateSsn" :name="`applicant_${i}_ssn`" :ssn-not-to-be-repeated="castedApplicantsSSNs" required/>
      </div>
      <div class="flex-1">
        <Field v-model="applicant.is_address_same_as_main" :name="`applicant_${i}_is_address_same_as_main`" v-slot="{ field, meta, errorMessage }" :rules="{ yes_no_required: true }" class="relative">
          <QuestionYesNo question-text="" id="sameAsMain" @update:modelValue="(v?: boolean) => sameAsMainChange(applicant, v)" name="sameAsMain" v-model="field.value">
            <template #question>
              <span>This person lives at the primary applicant's address</span>
              <VTooltip class="inline-block ml-2">
                <hfc-icon icon="question-circle" tabindex="-1"/>
                <template #popper>
                  <AddressValidationElement v-if="primaryApplicant?.main_address" :line-one="primaryApplicant?.main_address.line_one" :line-two="primaryApplicant?.main_address.line_two" :city="primaryApplicant?.main_address.city" :state="primaryApplicant?.main_address.state" :zip="`${primaryApplicant?.main_address.zip_code}`" :id="null" title="" :active="false" class="md:w-full text-[white!important]"/>
                </template>
              </VTooltip>
            </template>
          </QuestionYesNo>
          <span class="error"><span v-if="meta.touched">{{ errorMessage }}</span></span>
        </Field>
      </div>
      <div class="flex-1">
        <Field v-model="applicant.eligible_medicare" :name="`applicant_${i}_eligible_medicare`" v-slot="{ field, meta, errorMessage }" :rules="{ required: true }">
          <QuestionEligibleMedicare v-model="field.value" @update:model-value="(v?: MEDICARE_STATUSES) => updateEligibleMedicare(applicant, v)" />
          <span class="error"><span v-if="meta.touched">{{ errorMessage }}</span></span>
        </Field>
      </div>
      <hr class="mb-6 bg-blue-200 h-0.5" v-if="(i + 1) <  notPrimaryApplicants.length">
    </div>
  </BaseSection>
</template>
